import React, { useEffect, useRef, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import useIsMobile from "../../hooks/useIsMobile.hook";

const RADIAN = Math.PI / 180;
const MAX_SCORE = 10;

// Needle function
const needle = (
  value: number,
  cx: number,
  cy: number,
  iR: number,
  oR: number,
  color: string
): React.ReactNode[] => {
  const ang = 180.0 * (1 - value / MAX_SCORE);
  const length = (iR + 2 * oR) / 3;
  const sin = Math.sin(-RADIAN * ang);
  const cos = Math.cos(-RADIAN * ang);
  const r = 5;
  const x0 = cx;
  const y0 = cy;
  const xba = x0 + r * sin;
  const yba = y0 - r * cos;
  const xbb = x0 - r * sin;
  const ybb = y0 + r * cos;
  const xp = x0 + length * cos;
  const yp = y0 + length * sin;

  return [
    <circle
      key="needle-base"
      cx={x0}
      cy={y0}
      r={r}
      fill={color}
      stroke="none"
    />,
    <path
      key="needle"
      d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} Z`}
      stroke="none"
      fill={color}
    />,
  ];
};

// Props type
interface ScoreChartProps {
  averageScore: number;
  totalRecordsRated: number;
}

export default function ScoreChart({
  averageScore,
  totalRecordsRated,
}: ScoreChartProps) {
  const isMobile = useIsMobile();
  const safeScore = Math.max(0, Math.min(MAX_SCORE, averageScore));

  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const data = [
    { name: "Score", value: safeScore, color: "#ABB5DC" },
    { name: "Remaining", value: MAX_SCORE - safeScore, color: "#EEEEEE" },
  ];

  const renderGaugeTicks = (tickCount = 11): JSX.Element[] => {
    const ticks: JSX.Element[] = [];
    const radius = outerRadius + 12; // slightly below the arc
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height - 7;

    for (let i = 0; i < tickCount; i++) {
      const value = i; // 0 to 10
      const angle = 180 - (i * 180) / (tickCount - 1); // map from 0 to 180 deg
      const rad = angle * RADIAN;
      const x = centerX + radius * Math.cos(rad);
      const y = centerY - radius * Math.sin(rad);

      ticks.push(
        <text
          key={`tick-${i}`}
          x={x}
          y={y - 50}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="10"
          fill="#626262"
        >
          {value}
        </text>
      );
    }

    return ticks;
  };

  // Pie center and radius will be calculated based on container size
  const radiusBase =
    Math.min(dimensions.width * 1.5, dimensions.height * 1.5) / 2;
  const innerRadius = radiusBase * 0.65;
  const outerRadius = radiusBase * 0.75;
  return (
    <div
      ref={containerRef}
      data-testid="chart-container"
      style={{
        position: "relative",
        width: "100%",
        height: isMobile ? "250px" : "300px",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ bottom: 50, top: -50 }}>
          <Pie
            dataKey="value"
            startAngle={180}
            endAngle={0}
            data={data}
            cx="50%" // center horizontally
            cy="100%" // bottom center
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            stroke="none"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>

          {/* Note: needle needs fixed coordinates, so we handle separately */}
          {/* Commenting this code, As we have added tick from 0 to 10  */}
          {/* <text
            x={dimensions.width * 0.22}
            y={dimensions.height * 0.95}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fill="#626262"
          >
            0
          </text>
          <text
            x={dimensions.width * 0.78}
            y={dimensions.height * 0.95}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fill="#626262"
          >
            10
          </text> */}
          {renderGaugeTicks()}
        </PieChart>
      </ResponsiveContainer>

      {/* Needle: absolutely positioned over the chart */}
      <svg
        style={{
          position: "absolute",
          top: -50,
          left: "auto",
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {needle(
          safeScore,
          dimensions.width / 2,
          dimensions.height,
          innerRadius,
          outerRadius,
          "#1C359A"
        )}
        {/* centerX = 150 (half width), centerY = 300 (bottom), adjust inner/outer radius */}
      </svg>

      {/* Center label: Score */}
      <div
        style={{
          position: "absolute",
          top: isMobile ? "80%" : "90%",
          width: "100%",
          textAlign: "center",
          fontSize: "18px",
          fontWeight: "bold",
          color: "#626262",
        }}
      >
        Score: {Number.isInteger(safeScore) ? safeScore : safeScore.toFixed(1)}
        <div>
          Total Translations Rated:{" "}
          {Number.isInteger(totalRecordsRated)
            ? totalRecordsRated
            : totalRecordsRated.toFixed(1)}
        </div>
      </div>
    </div>
  );
}
